import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';

import { AlertOptions, ConfirmAutoCloseOptions, ConfirmOptions, PromptOptions } from './types';

type AlertModalProps = Omit<AlertOptions, 'type'> & {
  open: boolean;
  onClose: () => void;
};

export const AlertModal: FC<AlertModalProps> = ({
  title,
  message,
  buttonText,
  maxWidth = 'sm',
  open,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} maxWidth={maxWidth} fullWidth onClose={onClose}>
      {title && <DialogTitle>{title}</DialogTitle>}

      {message && (
        <DialogContent>
          {typeof message === 'string' ? (
            <Typography variant="body1">{message}</Typography>
          ) : (
            message
          )}
        </DialogContent>
      )}

      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {buttonText ?? t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DEFAULT_AUTO_CLOSE_DELAY_MS = 10_000;
const DEFAULT_AUTO_CLOSE_ACTION: ConfirmAutoCloseOptions['action'] = 'confirm';

type ConfirmModalProps = Omit<ConfirmOptions, 'type'> & {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export const ConfirmModal: FC<ConfirmModalProps> = ({
  title,
  message,
  submitButtonText,
  cancelButtonText,
  maxWidth = 'sm',
  autoClose,
  open,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();

  const totalMs =
    typeof autoClose === 'boolean'
      ? DEFAULT_AUTO_CLOSE_DELAY_MS
      : (autoClose?.closeDelay ?? DEFAULT_AUTO_CLOSE_DELAY_MS);

  const autoCloseAction =
    typeof autoClose === 'boolean'
      ? DEFAULT_AUTO_CLOSE_ACTION
      : (autoClose?.action ?? DEFAULT_AUTO_CLOSE_ACTION);

  const [remainingMs, setRemainingMs] = useState(totalMs);
  const rafRef = useRef<number | null>(null);
  const startedRef = useRef<number | null>(null);

  // Reset countdown when the dialog opens or total changes
  useEffect(() => {
    if (!autoClose) {
      return;
    }

    if (!open) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = null;
      startedRef.current = null;
      return;
    }

    setRemainingMs(totalMs);

    const tick = (now: number) => {
      if (!startedRef.current) {
        startedRef.current = now;
      }

      const elapsed = now - startedRef.current;
      const remain = Math.max(0, totalMs - elapsed);
      setRemainingMs(remain);

      if (remain <= 0) {
        autoCloseAction === 'confirm' ? onConfirm() : onCancel();
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = null;
      startedRef.current = null;
    };
  }, [open, totalMs, autoClose, autoCloseAction, onConfirm, onCancel]);

  // Progress (0..100). If disabled, keep undefined to hide bar.
  const progress = autoClose
    ? Math.min(100, Math.round(((totalMs - remainingMs) / totalMs) * 100))
    : undefined;

  const secondsLeft = Math.ceil(Math.max(0, remainingMs) / 1000);

  return (
    <Dialog
      maxWidth={maxWidth}
      open={open}
      fullWidth
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
          onCancel();
        }
      }}
    >
      {title && <DialogTitle>{title}</DialogTitle>}

      <DialogContent>
        {typeof message === 'string' ? (
          <Typography variant="body1" sx={{ mb: progress !== undefined ? 1.5 : 0 }}>
            {message}
          </Typography>
        ) : (
          message
        )}

        {progress !== undefined && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate" value={progress} aria-label={t('progress')} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 0.5,
                color: 'text.secondary',
              }}
            >
              <Typography variant="caption">
                {autoCloseAction === 'confirm'
                  ? t('autoContinueInXSeconds', { seconds: secondsLeft })
                  : t('autoCancelInXSeconds', { seconds: secondsLeft })}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={onCancel}>
          {cancelButtonText ?? t('cancel')}
        </Button>

        <Button variant="contained" autoFocus onClick={onConfirm}>
          {submitButtonText ?? t('continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type PromptModalProps = Omit<PromptOptions, 'type'> & {
  open: boolean;
  onCancel: () => void;
  onSubmit: (value?: string) => void;
};

export const PromptModal: FC<PromptModalProps> = ({
  title,
  label,
  initialValue,
  inputType = 'text',
  multiline,
  submitButtonText,
  cancelButtonText,
  maxWidth = 'sm',
  open,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue ?? '');

  return (
    <Dialog maxWidth={maxWidth} fullWidth open={open} onClose={() => onCancel()}>
      {title && <DialogTitle>{title}</DialogTitle>}

      <DialogContent>
        <TextField
          label={label}
          value={value}
          type={multiline ? undefined : inputType}
          multiline={multiline}
          fullWidth
          autoFocus
          onChange={(e) => setValue(e.target.value)}
        />
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={onCancel}>
          {cancelButtonText ?? t('cancel')}
        </Button>
        <Button color="primary" variant="contained" onClick={() => onSubmit(value)}>
          {submitButtonText ?? t('submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
